import Loader from '@/components/loader/Loader.vue';
import ScreenshotCard from './components/ScreenshotCard.vue';
import ScreenshotModal from './components/ScreenshotModal.vue';

// LocalStorage keys
const STORAGE_KEYS = {
  REBOOT_QUEUE: 'digitalSignageRebootQueue',
  MENU_OPEN: 'digitalSignageRebootMenuOpen',
  LAST_REBOOT_AT: 'digitalSignageLastRebootAddedAt',
};

// Timer constants (in milliseconds)
const TIMERS = {
  AUTO_FETCH_DELAY: 300000, // 5 minutes
  MENU_HIDE_DELAY: 1200000, // 20 minutes
  TOTAL_QUEUE_LIFETIME: 1500000, // 25 minutes (5 + 20)
};

export default {
  name: 'BuddyDigitalSignageScreenshots',
  components: {
    Loader,
    ScreenshotCard,
    ScreenshotModal,
  },
  data() {
    let rebootQueue = [];
    let rebootMenuOpen = false;
    let lastRebootAddedAt = null;

    try {
      const storedQueue = localStorage.getItem(STORAGE_KEYS.REBOOT_QUEUE);
      const storedMenuOpen = localStorage.getItem(STORAGE_KEYS.MENU_OPEN);
      const storedLastRebootAt = localStorage.getItem(STORAGE_KEYS.LAST_REBOOT_AT);

      rebootQueue = storedQueue ? JSON.parse(storedQueue) : [];
      rebootMenuOpen = storedMenuOpen === 'true';
      lastRebootAddedAt = storedLastRebootAt ? parseInt(storedLastRebootAt, 10) : null;
    } catch (error) {
      console.error('Error loading reboot queue from localStorage:', error);
    }

    return {
      search: '',
      imageDialog: false,
      selectedScreenshot: null,
      rebootQueue,
      rebootMenuOpen,
      lastRebootAddedAt,
      autoFetchTimeoutId: null as null | number,
      menuHideTimeoutId: null as null | number,
      nowTick: Date.now(),
      nowTickIntervalId: null as null | number,
      autoFetchCompleted: false,
    };
  },
  watch: {
    rebootMenuOpen() {
      this.saveMenuStateToStorage();
    },
  },
  computed: {
    loading() {
      return this.$store.state.digitalSignageScreenshotsModule.loading;
    },
    fetchingScreenshots() {
      return this.$store.state.digitalSignageScreenshotsModule.fetchingScreenshots;
    },
    screenshots() {
      return this.$store.state.digitalSignageScreenshotsModule.screenshots;
    },
    captureStatus() {
      return this.$store.state.digitalSignageScreenshotsModule.captureStatus;
    },
    captureError() {
      return this.$store.state.digitalSignageScreenshotsModule.captureError;
    },
    loadError() {
      return this.$store.state.digitalSignageScreenshotsModule.loadError;
    },
    totalDisplays() {
      return this.screenshots.length;
    },
    autoFetchCountdown() {
      if (!this.lastRebootAddedAt) return null;
      const remaining = TIMERS.AUTO_FETCH_DELAY - (this.nowTick - this.lastRebootAddedAt);
      return this.formatCountdown(remaining);
    },
    filteredScreenshots() {
      let filtered = [...this.screenshots];

      // Apply search filter
      if (this.search) {
        const searchLower = this.search.toLowerCase();
        filtered = filtered.filter(
          (s: any) =>
            s.deviceName?.toLowerCase().includes(searchLower) ||
            s.displayName?.toLowerCase().includes(searchLower) ||
            s.name?.toLowerCase().includes(searchLower) ||
            s.deviceId?.toLowerCase().includes(searchLower) ||
            s.facilityName?.toLowerCase().includes(searchLower) ||
            s.serial?.toLowerCase().includes(searchLower)
        );
      }

      return filtered;
    },
  },
  async created() {
    await this.$store.dispatch('getDigitalSignageScreenshots');
    this.nowTickIntervalId = window.setInterval(() => {
      this.nowTick = Date.now();
    }, 1000);

    // Restore timers if there's an active reboot queue
    if (this.rebootQueue.length > 0 && this.lastRebootAddedAt) {
      const timeSinceLastReboot = Date.now() - this.lastRebootAddedAt;

      // If less than 3 minutes have passed, schedule auto-fetch
      if (timeSinceLastReboot < TIMERS.AUTO_FETCH_DELAY) {
        const remainingTime = TIMERS.AUTO_FETCH_DELAY - timeSinceLastReboot;
        this.autoFetchTimeoutId = window.setTimeout(async () => {
          await this.fetchAllScreenshots();
          this.scheduleMenuHide();
        }, remainingTime);
      }
      // If between 3-13 minutes, schedule menu hide with remaining time
      else if (timeSinceLastReboot < TIMERS.TOTAL_QUEUE_LIFETIME) {
        const remainingTime = TIMERS.TOTAL_QUEUE_LIFETIME - timeSinceLastReboot;
        this.menuHideTimeoutId = window.setTimeout(() => {
          this.rebootMenuOpen = false;
          this.rebootQueue = [];
          this.lastRebootAddedAt = null;
          this.autoFetchTimeoutId = null;
          this.clearRebootQueueStorage();
        }, remainingTime);
      }
      // If more than 13 minutes, clear the queue (it's outdated)
      else {
        this.clearChecklist();
      }
    }
  },
  beforeDestroy() {
    if (this.autoFetchTimeoutId) clearTimeout(this.autoFetchTimeoutId);
    if (this.menuHideTimeoutId) clearTimeout(this.menuHideTimeoutId);
    if (this.nowTickIntervalId) clearInterval(this.nowTickIntervalId);
  },
  methods: {
    async fetchAllScreenshots() {
      await this.$store.dispatch('captureDeviceScreenshots');
    },
    openFullImage(screenshot: any) {
      this.selectedScreenshot = screenshot;
      this.imageDialog = true;
    },
    openQueuedScreenshot(deviceId: string) {
      if (!deviceId) return;
      // Close modal first to force refresh
      this.imageDialog = false;
      this.$nextTick(() => {
        const match = this.screenshots.find((s: any) => s.deviceId === deviceId);
        if (match) {
          this.openFullImage(match);
        }
      });
    },
    queueRebootedDevice(payload: { deviceId: string; displayName: string }) {
      if (!payload?.deviceId) return;
      const now = Date.now();
      this.lastRebootAddedAt = now;
      this.autoFetchCompleted = false;
      this.rebootQueue = [
        { ...payload, addedAt: now },
        ...this.rebootQueue.filter((item) => item.deviceId !== payload.deviceId),
      ];
      this.saveRebootQueueToStorage();
      this.ensureMenuVisible();
      this.scheduleAutoFetch();
    },
    scheduleAutoFetch() {
      if (this.autoFetchTimeoutId) clearTimeout(this.autoFetchTimeoutId);
      this.autoFetchTimeoutId = window.setTimeout(async () => {
        await this.fetchAllScreenshots();
        this.autoFetchCompleted = true;
        this.scheduleMenuHide();
        window.location.reload();
      }, TIMERS.AUTO_FETCH_DELAY);
    },
    async manualRefetch() {
      this.autoFetchCompleted = false;
      await this.fetchAllScreenshots();
      this.autoFetchCompleted = true;
      window.location.reload();
    },
    scheduleMenuHide() {
      if (this.menuHideTimeoutId) clearTimeout(this.menuHideTimeoutId);
      this.menuHideTimeoutId = window.setTimeout(() => {
        this.rebootMenuOpen = false;
        this.rebootQueue = [];
        this.lastRebootAddedAt = null;
        this.autoFetchTimeoutId = null;
        this.clearRebootQueueStorage();
      }, TIMERS.MENU_HIDE_DELAY);
    },
    removeFromQueue(deviceId: string) {
      this.rebootQueue = this.rebootQueue.filter((item) => item.deviceId !== deviceId);

      if (this.rebootQueue.length === 0) {
        // If queue is now empty, clear everything
        this.clearChecklist();
      } else {
        // Update the lastRebootAddedAt to the most recent item in the queue
        const mostRecentItem = this.rebootQueue.reduce((latest, item) =>
          item.addedAt > latest.addedAt ? item : latest
        );
        this.lastRebootAddedAt = mostRecentItem.addedAt;
        this.saveRebootQueueToStorage();
      }
    },
    clearChecklist() {
      this.rebootQueue = [];
      this.rebootMenuOpen = false;
      this.lastRebootAddedAt = null;
      this.autoFetchCompleted = false;
      if (this.autoFetchTimeoutId) clearTimeout(this.autoFetchTimeoutId);
      if (this.menuHideTimeoutId) clearTimeout(this.menuHideTimeoutId);
      this.autoFetchTimeoutId = null;
      this.menuHideTimeoutId = null;
      this.clearRebootQueueStorage();
    },
    ensureMenuVisible() {
      this.rebootMenuOpen = this.rebootQueue.length > 0;
      this.saveMenuStateToStorage();
    },
    saveRebootQueueToStorage() {
      localStorage.setItem(STORAGE_KEYS.REBOOT_QUEUE, JSON.stringify(this.rebootQueue));
      localStorage.setItem(STORAGE_KEYS.LAST_REBOOT_AT, String(this.lastRebootAddedAt || ''));
    },
    saveMenuStateToStorage() {
      localStorage.setItem(STORAGE_KEYS.MENU_OPEN, String(this.rebootMenuOpen));
    },
    clearRebootQueueStorage() {
      localStorage.removeItem(STORAGE_KEYS.REBOOT_QUEUE);
      localStorage.removeItem(STORAGE_KEYS.MENU_OPEN);
      localStorage.removeItem(STORAGE_KEYS.LAST_REBOOT_AT);
    },
    formatCountdown(ms: number) {
      const clamped = Math.max(0, ms);
      const totalSeconds = Math.floor(clamped / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    },
  },
};
